import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import MaterialIcon from '@/components/ui/MaterialIcon';
import AppShell from '@/components/layout/AppShell';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { useAuthStore } from '@/store/authStore';
import authService from '@/services/authService';
import { changePasswordSchema, type ChangePasswordFormData } from '@/utils/validators';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import IOSInstallInstructions from '@/components/pwa/IOSInstallInstructions';
import toast from 'react-hot-toast';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isIOSInstructionsOpen, setIsIOSInstructionsOpen] = useState(false);
  const { canPrompt, isIOS, isInstalled, promptInstall } = useInstallPrompt();
  const canInstall = !isInstalled && (canPrompt || isIOS);

  const handleInstall = async () => {
    if (canPrompt) {
      const accepted = await promptInstall();
      if (accepted) toast.success('Application installée');
    } else if (isIOS) {
      setIsIOSInstructionsOpen(true);
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
  });

  const changePasswordMutation = useMutation({
    mutationFn: (data: ChangePasswordFormData) =>
      authService.changePassword(data.currentPassword, data.newPassword),
    onSuccess: () => {
      toast.success('Mot de passe mis à jour');
      reset();
      setIsPasswordModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error?.message ?? 'Erreur lors du changement de mot de passe');
    },
  });

  const closePasswordModal = () => {
    reset();
    setIsPasswordModalOpen(false);
  };

  return (
    <AppShell title="Paramètres" showBack>
      <div className="flex flex-col gap-6 p-4 md:p-0 md:pt-4 md:max-w-2xl">
        <section>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3 px-2">Compte</h3>
          <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-sm border border-gray-100 dark:border-white/5 overflow-hidden">
            <div className="flex items-center gap-4 p-4 border-b border-gray-100 dark:border-white/5">
              <div className="size-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div className="flex flex-col flex-1">
                <span className="font-medium text-slate-900 dark:text-white">{user?.firstName} {user?.lastName}</span>
                <span className="text-xs text-gray-500">{user?.email}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate('/profile')}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <MaterialIcon icon="person" className="text-gray-400" />
                <span className="text-slate-900 dark:text-white font-medium">Modifier mon profil</span>
              </div>
              <MaterialIcon icon="chevron_right" className="text-gray-400" />
            </button>

            <button
              type="button"
              onClick={() => setIsPasswordModalOpen(true)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors border-t border-gray-100 dark:border-white/5"
            >
              <div className="flex items-center gap-3">
                <MaterialIcon icon="lock" className="text-gray-400" />
                <span className="text-slate-900 dark:text-white font-medium">Changer de mot de passe</span>
              </div>
              <MaterialIcon icon="chevron_right" className="text-gray-400" />
            </button>
          </div>
        </section>

        {canInstall && (
          <section>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3 px-2">Application</h3>
            <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-sm border border-gray-100 dark:border-white/5 overflow-hidden">
              <button
                type="button"
                onClick={handleInstall}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <MaterialIcon icon="install_mobile" className="text-primary" />
                  <div className="flex flex-col text-left">
                    <span className="text-slate-900 dark:text-white font-medium">
                      Installer l'application
                    </span>
                    <span className="text-xs text-gray-500">
                      {isIOS
                        ? "Ajouter Leeris à votre écran d'accueil iPhone"
                        : 'Accès rapide et hors ligne à votre carte de tiers payant'}
                    </span>
                  </div>
                </div>
                <MaterialIcon icon="chevron_right" className="text-gray-400" />
              </button>
            </div>
          </section>
        )}

        <section>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3 px-2">Aide</h3>
          <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-sm border border-gray-100 dark:border-white/5 overflow-hidden">
            <button
              type="button"
              onClick={() => navigate('/support')}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <MaterialIcon icon="headset_mic" className="text-gray-400" />
                <span className="text-slate-900 dark:text-white font-medium">Contacter le support</span>
              </div>
              <MaterialIcon icon="chevron_right" className="text-gray-400" />
            </button>
            <div className="p-4 flex items-center justify-between border-t border-gray-100 dark:border-white/5">
              <span className="text-gray-500">Version</span>
              <span className="text-gray-900 dark:text-white font-medium">1.0.0</span>
            </div>
          </div>
        </section>

        <button
          type="button"
          onClick={() => {
            useAuthStore.getState().logout();
            navigate('/login');
          }}
          className="w-full p-4 rounded-xl bg-red-50 dark:bg-red-900/10 text-red-500 font-bold hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors flex items-center justify-center gap-2"
        >
          <MaterialIcon icon="logout" />
          Se déconnecter
        </button>
      </div>

      <Modal
        isOpen={isPasswordModalOpen}
        onClose={closePasswordModal}
        title="Changer de mot de passe"
        footer={
          <>
            <Button variant="outline" onClick={closePasswordModal}>Annuler</Button>
            <Button
              variant="primary"
              onClick={handleSubmit((data) => changePasswordMutation.mutate(data))}
              isLoading={changePasswordMutation.isPending}
            >
              Enregistrer
            </Button>
          </>
        }
      >
        <form className="space-y-4" onSubmit={handleSubmit((data) => changePasswordMutation.mutate(data))}>
          <Input
            type="password"
            label="Mot de passe actuel"
            {...register('currentPassword')}
            error={errors.currentPassword?.message}
          />
          <Input
            type="password"
            label="Nouveau mot de passe"
            {...register('newPassword')}
            error={errors.newPassword?.message}
          />
          <Input
            type="password"
            label="Confirmer le nouveau mot de passe"
            {...register('confirmPassword')}
            error={errors.confirmPassword?.message}
          />
        </form>
      </Modal>

      <IOSInstallInstructions
        isOpen={isIOSInstructionsOpen}
        onClose={() => setIsIOSInstructionsOpen(false)}
      />
    </AppShell>
  );
};

export default SettingsPage;
